import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Stamp, CheckCircle2, ShieldCheck } from "lucide-react";

const fmt = (c, a) => `${c || "USD"} ${Number(a || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function NotaryPay() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [paid, setPaid] = useState(false);
  const [working, setWorking] = useState(false);

  const load = () => api.get(`/notary/pay/${token}`).then(({ data }) => { setInv(data); setPaid(data.payment_status === "paid"); }).catch((e) => setErr(extractErrorMessage(e, "Invoice not found.")));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (err) return <div className="mx-auto max-w-lg py-16 text-center" data-testid="notary-pay-error"><p className="text-slate-600">{err}</p></div>;
  if (!inv) return <div className="mx-auto max-w-lg py-16 text-center text-slate-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-lg space-y-5 py-10" data-testid="notary-pay-page">
      <div className="flex items-center gap-3"><Stamp className="h-7 w-7 text-indigo-600" /><div>
        <h2 className="font-display text-2xl font-bold text-slate-900">ARPS Notary Service Invoice</h2>
        <p className="text-slate-500">Secure payment for {inv.name}</p>
      </div></div>

      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5 text-sm">
          {inv.service_type && <div className="flex justify-between"><span className="text-slate-500">Service</span><span className="text-slate-800">{inv.service_type}</span></div>}
          <div className="flex justify-between"><span className="text-slate-500">Notary service fee</span><span data-testid="pay-service-fee" className="text-slate-800">{fmt(inv.currency, inv.service_fee)}</span></div>
          {inv.mobile_fee > 0 && <div className="flex justify-between"><span className="text-slate-500">Mobile service fee</span><span data-testid="pay-mobile-fee" className="text-slate-800">{fmt(inv.currency, inv.mobile_fee)}</span></div>}
          <div className="mt-2 flex justify-between border-t pt-2 text-base font-semibold"><span className="text-slate-900">Total due</span><span data-testid="pay-total" className="text-slate-900">{fmt(inv.currency, inv.invoice_total)}</span></div>
        </div>

        {paid ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="notary-pay-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="mt-2 font-semibold text-emerald-800">Payment received</p>
            <p className="text-sm text-emerald-700">Thank you. Our team will contact you to schedule your appointment.</p>
          </div>
        ) : !inv.paypal?.configured || !inv.invoice_total ? (
          <p className="rounded-lg bg-slate-50 p-3 text-center text-sm text-slate-500">Online payment is not available for this invoice yet.</p>
        ) : (
          <PayPalScriptProvider options={{ clientId: inv.paypal.client_id, currency: inv.currency || "USD", intent: "capture", components: "buttons" }}>
            <PayPalButtons
              style={{ layout: "vertical", label: "pay" }}
              disabled={working}
              forceReRender={[token, inv.invoice_total]}
              createOrder={async () => {
                setWorking(true);
                try { const { data } = await api.post(`/notary/pay/${token}/paypal/create-order`); return data.paypal_order_id; }
                catch (e) { setWorking(false); toast.error(extractErrorMessage(e, "Could not start checkout.")); throw e; }
              }}
              onApprove={async (data) => {
                try { const res = await api.post(`/notary/pay/${token}/paypal/capture`, { paypal_order_id: data.orderID });
                  if (res.data.payment_status === "paid") { setPaid(true); toast.success("Payment received"); } else toast.message("Payment recorded"); }
                catch (e) { toast.error(extractErrorMessage(e, "Payment could not be verified.")); }
                finally { setWorking(false); }
              }}
              onCancel={() => { setWorking(false); toast.message("Payment cancelled"); }}
              onError={() => { setWorking(false); toast.error("PayPal error. Please try again."); }}
            />
          </PayPalScriptProvider>
        )}
        <p className="flex items-center justify-center gap-1.5 text-center text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" />{inv.paypal?.environment === "sandbox" ? "PayPal Sandbox (test mode)" : "Secured by PayPal"}</p>
      </div>
    </div>
  );
}
